import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FileText,
  Plus,
  Calendar,
  TrendingUp,
  Star,
  Phone,
  Eye,
  MousePointer,
  MapPin,
  Copy,
  Check,
  BarChart3,
  Clock,
  Send,
  Settings2,
  Archive,
  X,
  Mail,
} from "lucide-react";
import { useLocationStore } from "@/lib/store";
import type { Location, MonthlyReport } from "@shared/schema";
import { useState, useEffect, lazy, Suspense } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const ClientReportPage = lazy(() => import("@/pages/client-report"));

type TabType = "new" | "all" | "schedules";

function MetricCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string | number; color: string }) {
  return (
    <div className="p-4 rounded-lg bg-muted/30 text-center">
      <div className={`w-8 h-8 rounded-lg mx-auto flex items-center justify-center mb-2 ${color}`}>
        {icon}
      </div>
      <p className="text-xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

function ReportDetailModal({
  report,
  open,
  onOpenChange,
}: {
  report: MonthlyReport;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const metrics = (report.metrics || {}) as Record<string, number>;

  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const monthName = monthNames[parseInt(report.month) - 1] || report.month;

  const shareUrl = `${window.location.origin}/share/report/${report.shareToken}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: "Share link copied!" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-brand-orange" />
            {monthName} {report.year} Report
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard icon={<Eye className="w-4 h-4 text-white" />} label="Views" value={metrics.views || 0} color="bg-brand-blue" />
          <MetricCard icon={<Phone className="w-4 h-4 text-white" />} label="Calls" value={metrics.calls || 0} color="bg-emerald-500" />
          <MetricCard icon={<MapPin className="w-4 h-4 text-white" />} label="Directions" value={metrics.directions || 0} color="bg-amber-500" />
          <MetricCard icon={<MousePointer className="w-4 h-4 text-white" />} label="Clicks" value={metrics.clicks || 0} color="bg-purple-500" />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <MetricCard icon={<Star className="w-4 h-4 text-white" />} label="Avg Rating" value={metrics.avgRating?.toFixed(1) || "0.0"} color="bg-amber-400" />
          <MetricCard icon={<TrendingUp className="w-4 h-4 text-white" />} label="Response Rate" value={`${metrics.responseRate || 0}%`} color="bg-brand-orange" />
          <MetricCard icon={<BarChart3 className="w-4 h-4 text-white" />} label="Total Reviews" value={metrics.totalReviews || 0} color="bg-brand-blue" />
        </div>

        {report.summary && (
          <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
            <h4 className="text-sm font-medium mb-2">AI Summary</h4>
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{report.summary}</p>
          </div>
        )}

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={handleCopyLink} data-testid="button-copy-share-link">
            {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
            {copied ? "Copied!" : "Share Link"}
          </Button>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AllReportsTab() {
  const { selectedLocationId } = useLocationStore();
  const { toast } = useToast();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return String(now.getMonth() + 1).padStart(2, "0");
  });
  const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
  const [viewReport, setViewReport] = useState<MonthlyReport | null>(null);

  const { data: locations } = useQuery<Location[]>({ queryKey: ["/api/locations"] });
  const locationId = selectedLocationId || locations?.[0]?.id;

  const { data: reports = [], isLoading } = useQuery<MonthlyReport[]>({
    queryKey: ["/api/reports", locationId],
    enabled: !!locationId,
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const result = await apiRequest("POST", "/api/reports/generate", {
        locationId,
        month: selectedMonth,
        year: selectedYear,
      });
      return result.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reports", locationId] });
      toast({ title: "Report generated successfully!" });
    },
    onError: () => {
      toast({ title: "Failed to generate report", variant: "destructive" });
    },
  });

  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const currentYear = new Date().getFullYear();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40" />
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-brand-orange/20 bg-brand-orange/5">
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold mb-4">Generate Monthly Snapshot</h3>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Month</label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-40" data-testid="select-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthNames.map((name, i) => (
                    <SelectItem key={i} value={String(i + 1).padStart(2, "0")}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Year</label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-28" data-testid="select-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
              className="bg-brand-orange hover:bg-brand-orange/90"
              data-testid="button-generate-report"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {generateMutation.isPending ? "Generating..." : "Generate"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground">
          Report Archive ({reports.length})
        </h3>
        {reports.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Archive className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold">No Reports Yet</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Generate your first monthly report to start building your archive.
              </p>
            </CardContent>
          </Card>
        ) : (
          reports.map((report) => {
            const monthName = monthNames[parseInt(report.month) - 1] || report.month;
            const metrics = (report.metrics || {}) as Record<string, number>;
            return (
              <Card key={report.id} className="hover:border-border transition-colors" data-testid={`card-report-${report.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-brand-orange/10 flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-brand-orange" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold">{monthName} {report.year}</h3>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {metrics.views || 0} views</span>
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {metrics.calls || 0} calls</span>
                          <span className="flex items-center gap-1"><Star className="w-3 h-3" /> {metrics.avgRating?.toFixed(1) || "0.0"}</span>
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setViewReport(report)} data-testid={`button-view-report-${report.id}`}>
                      <Eye className="w-3 h-3 mr-1" /> View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {viewReport && (
        <ReportDetailModal report={viewReport} open={!!viewReport} onOpenChange={(open) => !open && setViewReport(null)} />
      )}
    </div>
  );
}

interface ScheduleSettings {
  locationId: string;
  scheduleEnabled: boolean;
  frequency: string;
  dayOfWeek: string;
  scheduleTime: string;
  emailEnabled: boolean;
  emailTo: string[];
  emailCc: string[];
  emailBcc: string[];
  smsEnabled: boolean;
  smsTo: string[];
}

function EmailTagInput({ label, values, onChange }: { label: string; values: string[]; onChange: (v: string[]) => void }) {
  const [input, setInput] = useState("");

  const addEmail = () => {
    const trimmed = input.trim();
    if (trimmed && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
      setInput("");
    }
  };

  return (
    <div>
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <div className="flex gap-2 mt-1.5">
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
          placeholder="email@example.com"
          className="flex-1"
          data-testid={`input-${label.toLowerCase().replace(/\s/g, "-")}`}
        />
        <Button variant="outline" size="sm" onClick={addEmail} data-testid={`button-add-${label.toLowerCase().replace(/\s/g, "-")}`}>
          <Plus className="w-3 h-3" />
        </Button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {values.map((v, i) => (
            <Badge key={i} variant="secondary" className="text-xs gap-1">
              {v}
              <X className="w-3 h-3 cursor-pointer hover:text-red-400" onClick={() => onChange(values.filter((_, j) => j !== i))} />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function SchedulesTab() {
  const { selectedLocationId } = useLocationStore();
  const { toast } = useToast();

  const { data: locations } = useQuery<Location[]>({ queryKey: ["/api/locations"] });
  const locationId = selectedLocationId || locations?.[0]?.id;

  const { data: settings, isLoading } = useQuery<ScheduleSettings>({
    queryKey: ["/api/report-settings", locationId],
    queryFn: async () => {
      const res = await fetch(`/api/report-settings/${locationId}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    enabled: !!locationId,
  });

  const [local, setLocal] = useState<ScheduleSettings>({
    locationId: locationId || "",
    scheduleEnabled: false,
    frequency: "weekly",
    dayOfWeek: "monday",
    scheduleTime: "09:00",
    emailEnabled: false,
    emailTo: [],
    emailCc: [],
    emailBcc: [],
    smsEnabled: false,
    smsTo: [],
  });

  useEffect(() => {
    if (settings) setLocal({ ...settings, locationId: locationId || "" });
  }, [settings, locationId]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/report-settings/${locationId}`, local);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/report-settings", locationId] });
      toast({ title: "Schedule saved" });
    },
    onError: () => toast({ title: "Failed to save schedule", variant: "destructive" }),
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      const shareUrl = `${window.location.origin}/reports`;
      const res = await apiRequest("POST", `/api/client-report/${locationId}/send`, {
        emailTo: local.emailTo,
        emailCc: local.emailCc,
        emailBcc: local.emailBcc,
        smsTo: local.smsTo,
        shareUrl,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({ title: `Report sent: ${data.sent} delivered, ${data.failed} failed` });
    },
    onError: () => toast({ title: "Failed to send report", variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40" />
        <Skeleton className="h-60" />
      </div>
    );
  }

  const daysOfWeek = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  const timeSlots = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-brand-orange/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-brand-orange" />
              </div>
              <div>
                <h3 className="text-sm font-semibold">Automated Report Schedule</h3>
                <p className="text-xs text-muted-foreground">Automatically generate and send reports on a recurring basis</p>
              </div>
            </div>
            <Switch
              checked={local.scheduleEnabled}
              onCheckedChange={v => setLocal({ ...local, scheduleEnabled: v })}
              data-testid="switch-schedule-enabled"
            />
          </div>

          {local.scheduleEnabled && (
            <div className="space-y-4 pt-2 border-t">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">Frequency</Label>
                  <Select value={local.frequency} onValueChange={v => setLocal({ ...local, frequency: v })}>
                    <SelectTrigger data-testid="select-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">Day</Label>
                  <Select value={local.dayOfWeek} onValueChange={v => setLocal({ ...local, dayOfWeek: v })}>
                    <SelectTrigger data-testid="select-day-of-week">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {daysOfWeek.map(d => (
                        <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">Time</Label>
                  <Select value={local.scheduleTime} onValueChange={v => setLocal({ ...local, scheduleTime: v })}>
                    <SelectTrigger data-testid="select-time">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {local.scheduleEnabled && (
                <div className="p-3 rounded-lg bg-brand-orange/5 border border-brand-orange/20 text-xs text-muted-foreground">
                  Reports will be generated <span className="font-medium text-foreground">{local.frequency}</span> on <span className="font-medium text-foreground">{local.dayOfWeek.charAt(0).toUpperCase() + local.dayOfWeek.slice(1)}s</span> at <span className="font-medium text-foreground">{local.scheduleTime}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold">Email Delivery</h3>
                <p className="text-xs text-muted-foreground">Send reports automatically to clients and stakeholders</p>
              </div>
            </div>
            <Switch
              checked={local.emailEnabled}
              onCheckedChange={v => setLocal({ ...local, emailEnabled: v })}
              data-testid="switch-email-enabled"
            />
          </div>

          {local.emailEnabled && (
            <div className="space-y-4 pt-2 border-t">
              <EmailTagInput label="To" values={local.emailTo} onChange={v => setLocal({ ...local, emailTo: v })} />
              <EmailTagInput label="CC" values={local.emailCc} onChange={v => setLocal({ ...local, emailCc: v })} />
              <EmailTagInput label="BCC" values={local.emailBcc} onChange={v => setLocal({ ...local, emailBcc: v })} />
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="bg-brand-orange hover:bg-brand-orange/90"
          data-testid="button-save-schedule"
        >
          <Settings2 className="w-4 h-4 mr-1.5" />
          {saveMutation.isPending ? "Saving..." : "Save Schedule"}
        </Button>
        {local.emailEnabled && local.emailTo.length > 0 && (
          <Button
            variant="outline"
            onClick={() => sendMutation.mutate()}
            disabled={sendMutation.isPending}
            data-testid="button-send-now"
          >
            <Send className="w-4 h-4 mr-1.5" />
            {sendMutation.isPending ? "Sending..." : "Send Now"}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState<TabType>("new");

  const tabs: { key: TabType; label: string; icon: typeof FileText }[] = [
    { key: "new", label: "New Report", icon: BarChart3 },
    { key: "all", label: "All Reports", icon: Archive },
    { key: "schedules", label: "Schedules", icon: Clock },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="shrink-0 px-6 pt-6 pb-0">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-5">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="text-reports-title">
              <FileText className="w-6 h-6 text-brand-orange" />
              Reports
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              View, generate, and schedule performance reports
            </p>
          </div>
        </div>

        <div className="flex gap-1 border-b" data-testid="report-tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.key
                  ? "border-brand-orange text-brand-orange"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === "new" && (
          <Suspense fallback={
            <div className="p-6 space-y-4">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-40" />
              <Skeleton className="h-64" />
            </div>
          }>
            <ClientReportPage />
          </Suspense>
        )}
        {activeTab === "all" && (
          <div className="p-6 pb-24">
            <AllReportsTab />
          </div>
        )}
        {activeTab === "schedules" && (
          <div className="p-6 pb-24">
            <SchedulesTab />
          </div>
        )}
      </div>
    </div>
  );
}
